import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Compare as CompareIcon,
  CompareArrows as CompareArrowsIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoCamera as PhotoCameraIcon,
  Note as NoteIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Vehicle, VehicleConditionAssessment } from '../../types';
import { useAppDispatch, useAppSelector } from '../../store';
import { 
  fetchAssessmentHistory, 
  deleteAssessment, 
  clearError,
  setCurrentAssessment 
} from '../../store/vehicleConditionAssessmentSlice';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AssessmentViewModal from './AssessmentViewModal';

interface AssessmentHistoryModalProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onEdit?: (assessment: VehicleConditionAssessment) => void;
  onCompare?: (assessment1: VehicleConditionAssessment, assessment2: VehicleConditionAssessment) => void;
}

const AssessmentHistoryModal: React.FC<AssessmentHistoryModalProps> = ({
  vehicle,
  open,
  onClose,
  onEdit,
  onCompare
}) => {
  const dispatch = useAppDispatch();
  const { assessmentHistory, deleting, error } = useAppSelector(state => state.vehicleConditionAssessments);

  // UI state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<VehicleConditionAssessment | null>(null);
  const [expandedDate, setExpandedDate] = useState<string | false>(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<VehicleConditionAssessment[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewAssessment, setViewAssessment] = useState<VehicleConditionAssessment | null>(null);
  
  // Filtering and sorting state
  const [filterType, setFilterType] = useState<'all' | 'depart' | 'retour' | 'other'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'good' | 'fair' | 'poor' | 'critical'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Load assessment history when modal opens
  useEffect(() => {
    if (open && vehicle) {
      dispatch(fetchAssessmentHistory(vehicle.id));
    }
  }, [open, vehicle, dispatch]);

  // Clear error when modal closes
  useEffect(() => {
    if (!open) {
      dispatch(clearError());
    }
  }, [open, dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, assessment: VehicleConditionAssessment) => {
    setAnchorEl(event.currentTarget);
    setSelectedAssessment(assessment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAssessment(null);
  };

  const handleView = () => {
    if (selectedAssessment) {
      setViewAssessment(selectedAssessment);
      setViewModalOpen(true);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedAssessment) {
      onEdit?.(selectedAssessment);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedAssessment) {
      try {
        await dispatch(deleteAssessment({ 
          vehicleId: vehicle.id, 
          assessmentId: selectedAssessment.id 
        })).unwrap();
        // Refresh history after deletion
        dispatch(fetchAssessmentHistory(vehicle.id));
      } catch (error) {
        console.error('Error deleting assessment:', error);
      }
    }
    handleMenuClose();
  };

  const handleCompare = () => {
    handleMenuClose();
    setCompareMode(true);
  };

  const handleSelectForCompare = (assessment: VehicleConditionAssessment) => {
    if (selectedForCompare.find(a => a.id === assessment.id)) {
      setSelectedForCompare(selectedForCompare.filter(a => a.id !== assessment.id));
    } else {
      if (selectedForCompare.length < 2) {
        const newSelection = [...selectedForCompare, assessment];
        setSelectedForCompare(newSelection);
        
        if (newSelection.length === 2) {
          onCompare?.(newSelection[0], newSelection[1]);
          setCompareMode(false);
          setSelectedForCompare([]);
        }
      }
    }
  };

  const handleCancelCompare = () => {
    setCompareMode(false);
    setSelectedForCompare([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return '#4caf50';
      case 'fair': return '#8bc34a';
      case 'poor': return '#ff9800';
      case 'critical': return '#f44336';
      default: return '#2196f3';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'good': return 'Bon';
      case 'fair': return 'Correct';
      case 'poor': return 'Mauvais';
      case 'critical': return 'Critique';
      default: return 'Inconnu';
    }
  };

  const renderAssessmentCard = (assessment: VehicleConditionAssessment) => {
    const isSelectedForCompare = selectedForCompare.find(a => a.id === assessment.id);
    
    return (
      <Card 
        key={assessment.id} 
        sx={{ 
          mb: 2,
          cursor: compareMode ? 'pointer' : 'default',
          border: isSelectedForCompare ? '3px solid #2196f3' : 'none',
          '&:hover': compareMode ? {
            boxShadow: 4,
            transform: 'scale(1.02)',
            transition: 'all 0.2s'
          } : {}
        }}
        onClick={() => compareMode && handleSelectForCompare(assessment)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: isSelectedForCompare ? '#2196f3' : getStatusColor(assessment.status) }}>
                {isSelectedForCompare ? <CompareIcon /> : <PhotoCameraIcon />}
              </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                État des lieux #{assessment.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(assessment.assessment_date), 'dd/MM/yyyy à HH:mm', { locale: fr })}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSelectedForCompare && (
              <Chip
                label={`Sélection ${selectedForCompare.findIndex(a => a.id === assessment.id) + 1}/2`}
                size="small"
                color="primary"
              />
            )}
            <Chip
              label={getStatusLabel(assessment.status)}
              size="small"
              sx={{ 
                backgroundColor: getStatusColor(assessment.status),
                color: 'white'
              }}
            />
            {!compareMode && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e, assessment);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Assessment Summary */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhotoCameraIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {assessment.photos?.length || 0} photos
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NoteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {assessment.notes?.length || 0} notes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {assessment.created_by_name || 'Agent inconnu'}
            </Typography>
          </Box>
        </Box>

        {/* Notes Preview */}
        {(assessment.general_notes || assessment.damage_notes || assessment.alert_notes) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Notes:
            </Typography>
            {assessment.general_notes && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Général:</strong> {assessment.general_notes.substring(0, 100)}
                {assessment.general_notes.length > 100 && '...'}
              </Typography>
            )}
            {assessment.damage_notes && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Dommages:</strong> {assessment.damage_notes.substring(0, 100)}
                {assessment.damage_notes.length > 100 && '...'}
              </Typography>
            )}
            {assessment.alert_notes && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Alertes:</strong> {assessment.alert_notes.substring(0, 100)}
                {assessment.alert_notes.length > 100 && '...'}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
    );
  };

  // Find the most recent depart and its corresponding retour for quick comparison
  // Logic: A vehicle can only have one depart at a time. The retour for that depart
  // will happen before the next depart. So we find the most recent depart and its retour.
  const findLatestDepartRetourPair = () => {
    const allAssessments = assessmentHistory ? Object.values(assessmentHistory).flat() : [];
    
    // Get all depart and retour assessments, sorted by date (newest first)
    const departAssessments = allAssessments
      .filter(a => a.assessment_type === 'depart')
      .sort((a, b) => {
        const dateA = new Date(a.assessment_date).getTime();
        const dateB = new Date(b.assessment_date).getTime();
        return dateB - dateA; // Newest first
      });
    
    const retourAssessments = allAssessments
      .filter(a => a.assessment_type === 'retour')
      .sort((a, b) => {
        const dateA = new Date(a.assessment_date).getTime();
        const dateB = new Date(b.assessment_date).getTime();
        return dateB - dateA; // Newest first
      });
    
    // Find the most recent depart
    const latestDepart = departAssessments[0];
    
    if (!latestDepart || retourAssessments.length === 0) {
      return null;
    }
    
    const departDate = new Date(latestDepart.assessment_date).getTime();
    
    // Find the retour that corresponds to this depart
    // Since you can't depart again until you return, the retour for this depart
    // will be the first retour that happens after this depart (and before the next depart, if any)
    // Sort retours by date ascending to find the first one after the depart
    const retoursAfterDepart = retourAssessments
      .filter(r => {
        const retourDate = new Date(r.assessment_date).getTime();
        return retourDate >= departDate;
      })
      .sort((a, b) => {
        const dateA = new Date(a.assessment_date).getTime();
        const dateB = new Date(b.assessment_date).getTime();
        return dateA - dateB; // Oldest first (first retour after depart)
      });
    
    // If there's a retour after the depart, use the first one (the one that corresponds to this depart)
    // Otherwise, if there are retours but none after this depart, 
    // it means this depart hasn't returned yet, so we can't compare
    if (retoursAfterDepart.length === 0) {
      return null; // No retour found for this depart yet
    }
    
    const matchingRetour = retoursAfterDepart[0]; // First retour after this depart
    
    return {
      depart: latestDepart,
      retour: matchingRetour
    };
  };

  const latestDepartRetourPair = findLatestDepartRetourPair();

  const renderHistoryByDate = () => {
    // Get all assessments from history
    const allAssessments = assessmentHistory ? Object.values(assessmentHistory).flat() : [];
    
    // Apply filters
    let filteredAssessments = allAssessments;
    
    // Filter by type
    if (filterType !== 'all') {
      if (filterType === 'depart') {
        filteredAssessments = filteredAssessments.filter(a => a.assessment_type === 'depart');
      } else if (filterType === 'retour') {
        filteredAssessments = filteredAssessments.filter(a => a.assessment_type === 'retour');
      } else if (filterType === 'other') {
        filteredAssessments = filteredAssessments.filter(a => !a.assessment_type || (a.assessment_type !== 'depart' && a.assessment_type !== 'retour'));
      }
    }
    
    // Filter by status
    if (filterStatus !== 'all') {
      filteredAssessments = filteredAssessments.filter(a => a.status === filterStatus);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredAssessments = filteredAssessments.filter(a => {
        const searchableText = [
          a.id.toString(),
          a.assessment_type || '',
          a.status,
          a.general_notes || '',
          a.damage_notes || '',
          a.alert_notes || '',
          a.created_by_name || '',
          ...(a.notes || []).map(n => n.content),
          ...(a.photos || []).map(p => p.description || '')
        ].join(' ').toLowerCase();
        return searchableText.includes(query);
      });
    }
    
    // Sort assessments
    filteredAssessments.sort((a, b) => {
      const dateA = new Date(a.assessment_date).getTime();
      const dateB = new Date(b.assessment_date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    // Group by date
    const assessmentsByDate: Record<string, VehicleConditionAssessment[]> = {};
    filteredAssessments.forEach(assessment => {
      const dateKey = format(new Date(assessment.assessment_date), 'yyyy-MM-dd');
      if (!assessmentsByDate[dateKey]) {
        assessmentsByDate[dateKey] = [];
      }
      assessmentsByDate[dateKey].push(assessment);
    });
    
    const dates = Object.keys(assessmentsByDate).sort((a, b) => {
      return sortOrder === 'newest' 
        ? new Date(b).getTime() - new Date(a).getTime()
        : new Date(a).getTime() - new Date(b).getTime();
    });
    
    if (dates.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            📸 Aucun état des lieux
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {filterType !== 'all' || filterStatus !== 'all' || searchQuery.trim() 
              ? 'Aucun résultat ne correspond aux filtres sélectionnés.'
              : 'Aucun état des lieux n\'a encore été créé pour ce véhicule.'}
          </Typography>
        </Box>
      );
    }

    return dates.map(date => {
      const assessments = assessmentsByDate[date];
      const formattedDate = format(new Date(date), 'EEEE dd MMMM yyyy', { locale: fr });
      
      // Group assessments by type and sort by ID (most recent first)
      const departAssessments = assessments
        .filter(a => a.assessment_type === 'depart')
        .sort((a, b) => b.id - a.id); // Most recent first
      const retourAssessments = assessments
        .filter(a => a.assessment_type === 'retour')
        .sort((a, b) => b.id - a.id); // Most recent first
      const otherAssessments = assessments.filter(a => !a.assessment_type || (a.assessment_type !== 'depart' && a.assessment_type !== 'retour'));
      
      return (
        <Accordion
          key={date}
          expanded={expandedDate === date}
          onChange={(_, isExpanded) => setExpandedDate(isExpanded ? date : false)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HistoryIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formattedDate}
              </Typography>
              <Chip
                label={`${assessments.length} état${assessments.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Quick Compare Button - Show when both depart and retour exist (can be on different dates) */}
            {latestDepartRetourPair && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CompareArrowsIcon />}
                  onClick={() => {
                    if (latestDepartRetourPair && onCompare) {
                      onCompare(latestDepartRetourPair.depart, latestDepartRetourPair.retour);
                    }
                  }}
                  sx={{
                    px: 3,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                    }
                  }}
                >
                  Comparer Départ ↔ Retour
                  {latestDepartRetourPair.depart.assessment_date !== latestDepartRetourPair.retour.assessment_date && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', opacity: 0.9 }}>
                      (dates différentes)
                    </Typography>
                  )}
                </Button>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Départ Column */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  🚗 Départ ({departAssessments.length})
                </Typography>
                {departAssessments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {departAssessments.map(assessment => renderAssessmentCard(assessment))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    Aucun état des lieux de départ
                  </Typography>
                )}
              </Box>

              {/* Retour Column */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                  🏁 Retour ({retourAssessments.length})
                </Typography>
                {retourAssessments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {retourAssessments.map(assessment => renderAssessmentCard(assessment))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                    Aucun état des lieux de retour
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Other Assessments (without type) */}
            {otherAssessments.length > 0 && (
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                  📋 Autres ({otherAssessments.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {otherAssessments.map(assessment => renderAssessmentCard(assessment))}
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 3, m: 2, height: '90vh' }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📊 Historique des États des Lieux - {vehicle.registration_number}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Compare Mode Banner */}
          {compareMode && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleCancelCompare}>
                  Annuler
                </Button>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Mode Comparaison ({selectedForCompare.length}/2 sélectionnés)
              </Typography>
              <Typography variant="body2">
                Cliquez sur 2 états des lieux pour les comparer
              </Typography>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Filtering and Sorting Controls */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FilterListIcon color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Filtres et Tri
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
                InputProps={{
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
              
              {/* Filter by Type */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  label="Type"
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="depart">Départ</MenuItem>
                  <MenuItem value="retour">Retour</MenuItem>
                  <MenuItem value="other">Autres</MenuItem>
                </Select>
              </FormControl>
              
              {/* Filter by Status */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  label="Statut"
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="good">Bon</MenuItem>
                  <MenuItem value="fair">Correct</MenuItem>
                  <MenuItem value="poor">Mauvais</MenuItem>
                  <MenuItem value="critical">Critique</MenuItem>
                </Select>
              </FormControl>
              
              {/* Sort Order */}
              <ToggleButtonGroup
                value={sortOrder}
                exclusive
                onChange={(_, value) => value && setSortOrder(value)}
                size="small"
                sx={{ height: '40px' }}
              >
                <ToggleButton value="newest">
                  <SortIcon sx={{ mr: 0.5 }} />
                  Plus récent
                </ToggleButton>
                <ToggleButton value="oldest">
                  <SortIcon sx={{ mr: 0.5, transform: 'rotate(180deg)' }} />
                  Plus ancien
                </ToggleButton>
              </ToggleButtonGroup>
              
              {/* Clear Filters */}
              {(filterType !== 'all' || filterStatus !== 'all' || searchQuery.trim()) && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setFilterType('all');
                    setFilterStatus('all');
                    setSearchQuery('');
                  }}
                >
                  Réinitialiser
                </Button>
              )}
            </Box>
          </Box>

          {/* History Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {renderHistoryByDate()}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={onClose}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ViewIcon sx={{ mr: 1 }} />
          Voir
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Modifier
        </MenuItem>
        <MenuItem onClick={handleCompare}>
          <CompareIcon sx={{ mr: 1 }} />
          Comparer
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Assessment View Modal */}
      <AssessmentViewModal
        assessment={viewAssessment}
        assessments={assessmentHistory ? Object.values(assessmentHistory).flat() : []}
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewAssessment(null);
        }}
        onEdit={onEdit}
        onCompare={onCompare}
      />
    </>
  );
};

export default AssessmentHistoryModal;
